import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FormField, TextInput, TextArea } from "../ui/FormField";
import ImageUpload from "../ui/ImageUpload";

const today = new Date().toISOString().split("T")[0];

const campaignSchema = z
  .object({
    name: z.string().min(1, "Please enter a campaign name"),
    description: z.string().min(1, "Please enter a description"),
    start_date: z
      .string()
      .min(1, "Please select a start date")
      .refine((v) => v >= today, "Start date must be today or in the future"),
    expiry_date: z.string().min(1, "Please select an expiry date"),
  })
  .refine((data) => data.expiry_date > data.start_date, {
    message: "Expiry date must be after the start date",
    path: ["expiry_date"],
  });

type CampaignSchema = z.infer<typeof campaignSchema>;

export interface CampaignFormValues {
  name: string;
  description: string;
  start_date: string;
  expiry_date: string;
  image_url: string;
}

interface CampaignFormProps {
  initialValues?: Partial<CampaignFormValues>;
  onSave: (values: CampaignFormValues) => void;
  onReset?: () => void;
  isPending?: boolean;
}

const CampaignForm = ({ initialValues, onSave, onReset, isPending }: CampaignFormProps) => {
  const [imageUrl, setImageUrl] = useState(initialValues?.image_url ?? "");
  const [imageError, setImageError] = useState("");
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CampaignSchema>({
    resolver: zodResolver(campaignSchema),
    defaultValues: {
      name: initialValues?.name ?? "",
      description: initialValues?.description ?? "",
      start_date: initialValues?.start_date ?? "",
      expiry_date: initialValues?.expiry_date ?? "",
    },
  });

  const onSubmit = (data: CampaignSchema) => {
    if (!imageUrl) {
      setImageError("Image is required");
      return;
    }
    onSave({
      name: data.name,
      description: data.description,
      start_date: new Date(data.start_date).toISOString(),
      expiry_date: new Date(data.expiry_date).toISOString(),
      image_url: imageUrl,
    });
  };

  const handleReset = () => {
    setImageUrl(initialValues?.image_url ?? "");
    setImageError("");
    setUploadKey((k) => k + 1);
    reset({
      name: initialValues?.name ?? "",
      description: initialValues?.description ?? "",
      start_date: initialValues?.start_date ?? "",
      expiry_date: initialValues?.expiry_date ?? "",
    });
    onReset?.();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="bg-white rounded-[8px] px-[33px] pt-[33px] pb-[49px] flex flex-col gap-6"
      style={{
        border: "1px solid rgba(194,199,201,0.1)",
        boxShadow: "0px 20px 25px -5px rgba(0,0,0,0.1), 0px 8px 10px -6px rgba(0,0,0,0.1)",
      }}
    >
      <FormField label="Campaign Name" error={errors.name?.message}>
        <TextInput {...register("name")} placeholder="e.g. Summer Sips" />
      </FormField>

      <FormField label="Campaign Description" error={errors.description?.message}>
        <TextArea {...register("description")} placeholder="Describe the campaign…" rows={3} />
      </FormField>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Start Date" error={errors.start_date?.message}>
          <TextInput type="date" {...register("start_date")} min={today} />
        </FormField>
        <FormField label="Expiry Date" error={errors.expiry_date?.message}>
          <TextInput type="date" {...register("expiry_date")} min={watch("start_date") || today} />
        </FormField>
      </div>

      <ImageUpload
        key={uploadKey}
        label="Campaign Image"
        initialUrl={initialValues?.image_url}
        onKeyChange={(key) => { setImageUrl(key); if (key) setImageError(""); }}
        onBusyChange={setIsImageUploading}
      />
      {imageError && <span className="text-[11px] text-content-error">{imageError}</span>}

      <p className="text-[13px] text-content-label leading-[1.6] rounded-[6px] bg-orange-50 px-4 py-3">
        <span className="font-semibold text-content-body">Note:</span> Creating a campaign does not automatically send it to customers. To notify customers, please click the <span className="font-semibold text-content-body">Send Email</span> button after creating the campaign.
      </p>

      <div className="flex flex-col gap-3 pt-4">
        <button
          type="submit"
          disabled={isPending || isImageUploading}
          className="w-full h-[45px] rounded-[20px] text-white text-[16px] font-bold tracking-[0.64px] transition-opacity hover:opacity-90 disabled:opacity-60 bg-brand-accent"
        >
          {isPending ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Saving…
            </span>
          ) : "Save"}
        </button>
        <button
          type="button"
          onClick={handleReset}
          className="w-full py-3 rounded-[6px] text-[14px] font-semibold text-center transition-opacity hover:opacity-70 text-brand-teal"
        >
          Reset Changes
        </button>
      </div>
    </form>
  );
};

export default CampaignForm;
